import React, {
    useCallback, useMemo, useState,
} from 'react'
import styles from 'styles/components/pages/transports/transport-list.module.scss'
import useTranslate from 'helpers/hooks/useTranslate'
import {
    Checkbox,
    ConstrainMode,
    DetailsListLayoutMode,
    DirectionalHint,
    SelectionMode,
    ShimmeredDetailsList,
    Text,
    TooltipDelay,
    TooltipHost,
    DefaultButton,
} from '@fluentui/react'
import history from 'helpers/history'
import Status from 'types/status'
import Time, { getLocaleFromLang } from 'helpers/methods/time'
import Card from 'components/containers/card'
import Transport from 'requests/objects/transport'
import parseJson from 'helpers/methods/parseJson'
import { wfTObj } from 'helpers/methods/getStepTypes'
import ConfirmedDateLogo from 'assets/img/confirmed-date.png'
import ScheduledDateLogo from 'assets/img/scheduled-date.png'
import getOrderTypeLogo from 'helpers/methods/getOrderTypeLogo'
import { ParamElement } from 'requests/objects/param'
import ECompany from 'types/companies/enums/company'
import { Translations } from 'types/translations'
import EProfile from 'types/users/enums/profile'

/**
 * TransportList
 * @param {object} props Props
 * @param {string} props.lang Lang
 * @param {Transport[]} props.transports transports
 * @param {Status} props.status status
 * @param {(orderBy: string) => void} props.onOrderByChange onOrderByChange
 * @param {ParamElement[]} props.workflowTransportSteps workflowTransportSteps
 * @param {number} props.companyId companyId
 * @param {object} props.me user connected
 * @param {Transport[]=} props.selectedTransports selectedTransports
 * @param {(selectedTransports: Transport[]) => void=} props.onSelectionChange onSelectionChange
 * @param {() => void=} props.onDownloadSummary onDownloadSummary
 * @param {() => void=} props.onDownloadLabels onDownloadLabels
 * @param {boolean=} props.isDownloadingDocuments isDownloadingDocuments
 * @returns {JSX.Element} Element
 */
export default function TransportList({
    lang, transports, status, onOrderByChange, workflowTransportSteps = [], companyId, me, selectedTransports = [], onSelectionChange,
    onDownloadSummary, onDownloadLabels, isDownloadingDocuments = false,
}) {
    const { tKey, tObj } = useTranslate({ lang })
    const [columnSort, setColumnSort] = useState(['', 'desc'])
    const locale = getLocaleFromLang(lang)
    const selectedTransportIds = useMemo(() => selectedTransports.map(x => x.transportId), [selectedTransports])

    const onCheckBoxChange = useCallback(transport => {
        if (selectedTransportIds.includes(transport.transportId)) {
            onSelectionChange?.(selectedTransports.filter(x => x.transportId !== transport.transportId))
            return
        }

        onSelectionChange?.([...selectedTransports, transport])
    }, [onSelectionChange, selectedTransportIds, selectedTransports])

    const columns = useMemo(() => /** @type {import('@fluentui/react').IColumn[]} */([
        {
            key: 'transportTmsId',
            name: tKey('transport'),
            fieldName: 'transportTmsId',
            minWidth: 150,
            maxWidth: 170,
            isResizable: true,
            isSorted: columnSort[0] === 'transportTmsId',
            isSortedDescending: columnSort[1] === 'desc',
            className: styles['transport-list-row-item'],
            onColumnClick: (ev, column) => {
                const sort = columnSort[0] !== 'transportTmsId' || column.isSortedDescending ? 'asc' : 'desc'
                const newColumnSort = [column.key, sort]
                setColumnSort(newColumnSort)
                onOrderByChange(newColumnSort.join('-'))
            },
            /**
             * @param {Transport} transport transport
             */
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => (
                    <TooltipHost
                        content={tObj(parseJson(transport['orderType.name']))}
                        directionalHint={DirectionalHint.leftBottomEdge}
                        delay={TooltipDelay.zero}
                    >
                        <div className={styles['transport-list-row-item-checked']}>
                            <Checkbox
                                checked={selectedTransportIds.includes(transport.transportId)}
                                onClick={ev => ev.stopPropagation()}
                                onChange={ev => {
                                    ev.stopPropagation()
                                    onCheckBoxChange(transport)
                                }}
                            />
                            <div className={styles['transport-list-row-item-logo']}>
                                <img
                                    src={getOrderTypeLogo(transport.orderTypeId)}
                                    alt="Logo type de livraison"
                                />
                                <span>{transport.transportTmsId}</span>
                            </div>
                        </div>
                    </TooltipHost>
                ),
        },

        ...([EProfile.Admin, EProfile.SuperAdmin].includes(me?.profileId) ? [{
            key: 'client.name',
            name: tKey('client'),
            fieldName: 'client.name',
            minWidth: 60,
            maxWidth: 80,
            isResizable: true,
            isSorted: columnSort[0] === 'client.name',
            isSortedDescending: columnSort[1] === 'desc',
            className: styles['transport-list-row-item'],
            onColumnClick: (ev, column) => {
                const sort = columnSort[0] !== 'client.name' || column.isSortedDescending ? 'asc' : 'desc'
                const newColumnSort = [column.key, sort]
                setColumnSort(newColumnSort)
                onOrderByChange(newColumnSort.join('-'))
            },
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => (
                    <div className={styles['client-cell']}>
                        <b>{transport['client.name'] || '-'}</b>
                    </div>
                ),
        }] : []),
        {
            key: 'contractor',
            name: tKey('refDo'),
            fieldName: 'contractor',
            minWidth: 60,
            maxWidth: 60,
            isResizable: true,
            isSorted: columnSort[0] === 'contractor',
            isSortedDescending: columnSort[1] === 'desc',
            className: styles['transport-list-row-item'],
            onColumnClick: (ev, column) => {
                const sort = columnSort[0] !== 'contractor' || column.isSortedDescending ? 'asc' : 'desc'
                const newColumnSort = [column.key, sort]
                setColumnSort(newColumnSort)
                onOrderByChange(newColumnSort.join('-'))
            },
        },
        {
            key: 'createdAt',
            name: tKey('createdThe'),
            minWidth: 60,
            maxWidth: 80,
            isResizable: true,
            className: styles['transport-list-row-item'],
            isSorted: columnSort[0] === 'createdAt',
            isSortedDescending: columnSort[1] === 'desc',
            onColumnClick: (ev, column) => {
                const sort = columnSort[0] !== 'createdAt' || column.isSortedDescending ? 'asc' : 'desc'
                const newColumnSort = [column.key, sort]
                setColumnSort(newColumnSort)
                onOrderByChange(newColumnSort.join('-'))
            },
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => (
                    transport.createdAt
                        ? Time(transport.createdAt, { locale }).getCleanDate({
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                        })
                        : ''
                ),

        },
        {
            key: 'pickupDate',
            name: tKey('pickupDate'),
            minWidth: 60,
            maxWidth: 105,
            isResizable: true,
            className: styles['transport-list-row-item'],
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => {
                    const date = transport.pickupDate || transport.pickupScheduledDate
                    if (!date)
                        return ''
                    return (
                        <TooltipHost
                            content={tKey(transport.pickupDate ? 'picked' : 'estimated')}
                            directionalHint={DirectionalHint.leftBottomEdge}
                            delay={TooltipDelay.zero}
                        >
                            <div className={styles['transport-list-row-item-date']}>
                                {transport.pickupDate ? (
                                    <img
                                        src={ConfirmedDateLogo}
                                        alt="Date de prise en charge"
                                    />

                                ) : (
                                    <img
                                        src={ScheduledDateLogo}
                                        alt="Date de prise en charge prévisionnelle"
                                    />
                                )}
                                <span>
                                    {Time(date, { locale }).getCleanDate({
                                        month: '2-digit',
                                        day: '2-digit',
                                        year: 'numeric',
                                    })}
                                </span>
                            </div>
                        </TooltipHost>
                    )
                },
            isSorted: columnSort[0] === 'pickupDate',
            isSortedDescending: columnSort[1] === 'desc',
            onColumnClick: (ev, column) => {
                const sort = columnSort[0] !== 'pickupDate' || column.isSortedDescending ? 'asc' : 'desc'
                const newColumnSort = [column.key, sort]
                setColumnSort(newColumnSort)
                onOrderByChange(newColumnSort.join('-'))
            },

        },
        {
            key: 'pickup',
            name: tKey('pickup'),
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            className: styles['transport-list-row-item'],
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => (
                    <>
                        <b>
                            {transport['pickupAddress.label'] || ''}
                        </b>
                        <span>
                            {transport['pickupAddress.street'] || ''}
                        </span>
                        <span>
                            {transport['pickupAddress.zipCode'] || ''}
                            {' '}
                            {transport['pickupAddress.city'] || ''}
                            {' '}
                            {transport['pickupAddress.country.code'] || ''}
                        </span>
                    </>
                ),
        },
        {
            key: 'deliveryDate',
            name: tKey('deliveryDate'),
            minWidth: 60,
            maxWidth: 105,
            isResizable: true,
            className: styles['transport-list-row-item'],
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => {
                    const date = transport.deliveryDate || transport.deliveryScheduledDate
                    if (!date)
                        return ''
                    return (
                        <TooltipHost
                            content={tKey(transport.deliveryDate ? 'delivered' : 'planned')}
                            directionalHint={DirectionalHint.leftBottomEdge}
                            delay={TooltipDelay.zero}
                        >
                            <div className={styles['transport-list-row-item-date']}>
                                {transport.deliveryDate ? (
                                    <img
                                        src={ConfirmedDateLogo}
                                        alt="Date de livraison"
                                    />

                                ) : (
                                    <img
                                        src={ScheduledDateLogo}
                                        alt="Date de livraison prévisionnelle"
                                    />
                                )}
                                <span>
                                    {Time(date, { locale }).getCleanDate({
                                        month: '2-digit',
                                        day: '2-digit',
                                        year: 'numeric',
                                    })}
                                </span>
                            </div>
                        </TooltipHost>
                    )
                },
            isSorted: columnSort[0] === 'deliveryDate',
            isSortedDescending: columnSort[1] === 'desc',
            onColumnClick: (ev, column) => {
                const sort = columnSort[0] !== 'deliveryDate' || column.isSortedDescending ? 'asc' : 'desc'
                const newColumnSort = [column.key, sort]
                setColumnSort(newColumnSort)
                onOrderByChange(newColumnSort.join('-'))
            },
        },
        {
            key: 'delivery',
            name: tKey('delivery'),
            minWidth: 80,
            maxWidth: 120,
            isResizable: true,
            className: styles['transport-list-row-item'],
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => (
                    <>
                        <b>
                            {transport['deliveryAddress.label'] || ''}
                        </b>
                        <span>
                            {transport['deliveryAddress.street'] || ''}
                        </span>
                        <span>
                            {transport['deliveryAddress.zipCode'] || ''}
                            {' '}
                            {transport['deliveryAddress.city'] || ''}
                            {' '}
                            {transport['deliveryAddress.country.code'] || ''}
                        </span>
                    </>
                ),
        },
        {
            key: 'status',
            name: tKey('status'),
            fieldName: 'status.name',
            minWidth: 100,
            maxWidth: 130,
            isResizable: true,
            className: styles['transport-list-row-item'],
            onRender: transport => tObj(parseJson(workflowTransportSteps.find(x => x.key === transport.currentWorkflowStepId)?.text)),
        },
        {
            key: 'comment',
            name: tKey('comments'),
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            className: styles['transport-list-row-item'],
            onRender:
                // eslint-disable-next-line react/no-unstable-nested-components
                transport => {
                    let labelToDisplay = wfTObj(lang, parseJson(transport['transportStatus.0.tmsTransportStatus.label'])) || ''

                    // TODO: Remove or update this Pineau-specific workaround when Pineau migrates to Phileas.
                    // This block is a temporary mapping for legacy Pineau TMS eventType/reasonType values.
                    // when pineau pineau livré: conforme ==> livré
                    const eventType = String(transport['transportStatus.0.tmsTransportStatus.eventType'] ?? '')
                    const reasonType = String(transport['transportStatus.0.tmsTransportStatus.reasonType'] ?? '')

                    if (companyId === ECompany.Pineau && eventType === '21' && reasonType === '310')
                        labelToDisplay = Translations.delivered?.[lang] ?? 'livré'
                    if (companyId === ECompany.Pineau && eventType === '13' && reasonType === '310')
                        labelToDisplay = Translations.picked?.[lang] ?? 'Enlevé'
                    return (
                        <>
                            <b>{labelToDisplay}</b>
                            <span>
                                {
                                    wfTObj(
                                        lang,
                                        parseJson(transport['transportStatus.0.tmsTransportStatus.subLabel']),
                                        parseJson(transport['transportStatus.0.args']),
                                    ) || ''
                                }
                            </span>
                        </>

                    )
                },
        },
    ]), [
        tKey, columnSort, onOrderByChange, tObj, workflowTransportSteps, lang, companyId, me?.profileId,
        locale, selectedTransportIds, onCheckBoxChange,
    ])

    return (
        <div className={styles['transport-list']}>
            <Card
                customHeader={(
                    <div className={styles['header-list']}>
                        <span>
                            {tKey('nbSelected', { nb: selectedTransports.length.toString() })}
                        </span>

                        <DefaultButton
                            iconProps={{ iconName: 'ArrowUpRight' }}
                            onClick={() => {
                                onSelectionChange?.(selectedTransports.length ? [] : transports)
                            }}
                        >
                            {
                                selectedTransports.length
                                    ? tKey('deselect', { nb: selectedTransports.length.toString() })
                                    : tKey('selectAllWithNb', { nb: transports.length.toString() })
                            }
                        </DefaultButton>
                        {selectedTransports.length > 0 && (
                            <div className={styles['transport-selection-actions']}>
                                <DefaultButton
                                    text={tKey('editSummary')}
                                    iconProps={{ iconName: 'Print' }}
                                    onClick={() => onDownloadSummary?.()}
                                    disabled={isDownloadingDocuments}
                                />

                                <DefaultButton
                                    text={tKey('labels')}
                                    iconProps={{ iconName: 'QRCode' }}
                                    onClick={() => onDownloadLabels?.()}
                                    disabled={isDownloadingDocuments}
                                />
                            </div>
                        )}
                    </div>
                )}
            >
                <ShimmeredDetailsList
                    items={transports}
                    columns={columns}
                    detailsListStyles={{
                        root: {
                            selectors: {
                                '& [role=grid]': {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'start',
                                    height: transports.length ? '52vh' : 'auto',
                                },
                            },
                        },
                        headerWrapper: {
                            flex: '0 0 auto',
                        },
                        contentWrapper: {
                            flex: '1 1 auto',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                        },
                    }}
                    selectionMode={SelectionMode.none}
                    onShouldVirtualize={() => true}
                    enableShimmer={status === Status.PENDING}
                    layoutMode={DetailsListLayoutMode.justified}
                    constrainMode={ConstrainMode.unconstrained}
                    onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props, styles: { root: { paddingTop: 0 } } })}
                    // eslint-disable-next-line react/no-unstable-nested-components
                    onRenderRow={(props, defaultRender) => {
                        const isSelectionControl = target => (
                            target.closest('.ms-Check')
                            || target.closest('.ms-Checkbox')
                            || target.closest('[role="checkbox"]')
                            || target.closest('[data-selection-toggle="true"]')
                        )

                        const goToDetail = ev => {
                            if (isSelectionControl(ev.target))
                                return

                            history.push(`/transports/${props.item?.transportId}`)
                        }

                        const onKeyDown = ev => {
                            if (isSelectionControl(ev.target))
                                return

                            if (ev.key !== 'Enter' && ev.key !== ' ')
                                return

                            ev.preventDefault()
                            history.push(`/transports/${props.item?.transportId}`)
                        }

                        return (
                            <div
                                className={styles['transport-list-row']}
                                onClick={goToDetail}
                                onKeyDown={onKeyDown}
                                role="link"
                                tabIndex={0}
                            >
                                {defaultRender(props)}
                            </div>
                        )
                    }}
                />
                {!transports.length && status !== Status.PENDING
                    && <Text styles={{ root: { fontStyle: 'italic', marginLeft: '1em' } }}>{tKey('noResultFound')}</Text>}
            </Card>
        </div>
    )
}
